import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'

import { Observable, map } from 'rxjs'

import { ApiHttpService } from '@flowx/designer/shared/api/api-http.service'

import {
  DataModelContext,
  EnumerationContext,
  ImageContext,
  InitialMessageRequest,
  InitialMessageResponse,
  Message,
  NotificationContent,
  NotificationContext,
  SendFeedbackPayload,
} from '../../../common/ai.dictionary'

export type GenerateEnumerationRequest = {
  question: string
  context: EnumerationContext
  session_id: string
  request_id: string
}

export type GenerateEnumerationResponse = {
  question: string
  context: EnumerationContext
  answer: EnumerationContext
  chat: string
  session_id: string
  request_id: string
  error_message: string
}

export type GenerateImageRequest = {
  question: string
  session_id: string
  request_id: string
}

export type GenerateImageResponse = {
  question: string
  answer: ImageContext
  chat: string
  session_id: string
  request_id: string
  error_message: string
}

export type GenerateSubstTagsRequest = {
  question: string
  context: EnumerationContext
  session_id: string
  request_id: string
}

export type GenerateSubstTagsResponse = {
  question: string
  context: EnumerationContext
  answer: EnumerationContext
  chat: string
  session_id: string
  request_id: string
  error_message: string
}

export type GenerateDocTemplateRequest = {
  question: string
  context: NotificationContext
  session_id: string
  request_id: string
}

export type GenerateDocTemplateResponse = {
  question: string
  answer: NotificationContent
  context: NotificationContext
  chat: string
  session_id: string
  request_id: string
  error_message: string
}

export type GenerateNotTemplateRequest = {
  question: string
  context: NotificationContext
  session_id: string
  request_id: string
}

export type GenerateNotTemplateResponse = {
  question: string
  answer: NotificationContent
  context: NotificationContext
  chat: string
  session_id: string
  request_id: string
  error_message: string
}

export type GenerateDataModelRequest = {
  question: string
  context: DataModelContext
  session_id: string
  request_id: string
}

export type GenerateDataModelResponse = {
  question: string
  answer: DataModelContext
  context: DataModelContext
  chat: string
  session_id: string
  request_id: string
  error_message: string
}

export type RequestArchitectPayload = {
  question: string
  action:
    | 'generate_data_model'
    | 'generate_document_template'
    | 'generate_notification_template'
    | 'translate_substitution_tags'
  context: any
  session_id?: string
  request_id?: string
  skip_greeting?: boolean
}

@Injectable({ providedIn: 'root' })
export class AiArchitectAPI extends ApiHttpService {
  #architect = `ai-engine/agents/architect`

  constructor() {
    super(inject('BASE_URL' as any), inject(HttpClient))
  }

  initialMessage(payload: InitialMessageRequest): Observable<InitialMessageResponse> {
    return this.post(`${this.#architect}/initial`, payload)
  }

  generateImage(payload: GenerateImageRequest): Observable<GenerateImageResponse> {
    return this.post(`${this.#architect}/image`, payload)
  }

  generateEnumeration(
    payload: GenerateEnumerationRequest
  ): Observable<GenerateEnumerationResponse> {
    return this.post(`${this.#architect}/enumeration`, payload)
  }

  generateDocumentTemplate(
    payload: GenerateDocTemplateRequest
  ): Observable<GenerateDocTemplateResponse> {
    return this.post(`${this.#architect}/document`, payload)
  }

  generateNotificationTemplate(
    payload: GenerateNotTemplateRequest
  ): Observable<GenerateNotTemplateResponse> {
    return this.post(`${this.#architect}/notification`, payload)
  }

  translateSubstitutionTags(
    payload: GenerateSubstTagsRequest
  ): Observable<GenerateSubstTagsResponse> {
    return this.post(`${this.#architect}/substitution_tags`, payload)
  }

  generateDataModel(payload: GenerateDataModelRequest): Observable<GenerateDataModelResponse> {
    return this.post(`${this.#architect}/data-model`, payload)
  }

  sendFeedback(payload: SendFeedbackPayload): Observable<string> {
    const { reason, feedbackType, message, requestId, sessionId } = payload

    return this.post(`${this.#architect}/feedback`, {
      feedback: {
        reason,
        response: feedbackType,
        user_message: message,
      },
      request_id: requestId,
      session_id: sessionId,
    })
  }
  getSSEActions(payload: InitialMessageRequest): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#architect}/actions`, payload).pipe(
      map((response: any) => {
        if (response) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)

          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      map((answerStr) => ({
        message: answerStr,
      }))
    )
  }

  getSSEGenerateArchitect(
    payload: RequestArchitectPayload
  ): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#architect}/chat`, payload).pipe(
      map((response: any) => {
        if (response) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)

          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      map((answerStr) => ({
        message: answerStr,
      }))
    )
  }
}
